import { useState } from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';

export interface SoftphoneKeypadProps {
  /** Fired once per key press with the single pressed digit (`0`-`9`, `*`, `#`). */
  onPressDigit: (digit: string) => void;
  /** Keys disabled (e.g. the call is not answered yet, so DTMF can't be sent). */
  disabled?: boolean;
  className?: string;
}

const KEYS: ReadonlyArray<{ digit: string; letters?: string }> = [
  { digit: '1' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*' },
  { digit: '0', letters: '+' },
  { digit: '#' },
];

/**
 * In-call DTMF keypad (IVR navigation: "press 1 for…"). Purely presentational — every press
 * fires `onPressDigit` with exactly one character. Digits are sent one per press, never batched:
 * Telnyx's `dtmf()` takes a single tone, while Twilio's `sendDigits()` would interpret a
 * multi-character string with its own pause semantics.
 */
export function SoftphoneKeypad({ onPressDigit, disabled, className }: SoftphoneKeypadProps) {
  // Visual feedback only — the tail of what was typed (e.g. an account number). Resets with the
  // component; the tones themselves are already sent per press.
  const [dialed, setDialed] = useState('');

  const press = (digit: string) => {
    setDialed((d) => d + digit);
    onPressDigit(digit);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)} data-test="keypad">
      <div
        className="flex h-5 items-center justify-end overflow-hidden text-sm tabular-nums text-gray-600 dark:text-gray-300"
        data-test="keypad-digits">
        {dialed}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {KEYS.map(({ digit, letters }) => (
          <Button
            key={digit}
            size="lg"
            textAlign="center"
            disabled={disabled}
            onClick={() => press(digit)}
            className="select-none"
            aria-label={`Key ${digit}`}
            data-test={`keypad-key-${digit}`}>
            <span className="flex flex-col items-center leading-tight">
              <span className="text-base font-semibold">{digit}</span>
              <span className="h-3 text-[9px] tracking-widest text-gray-500 dark:text-gray-400">
                {letters}
              </span>
            </span>
          </Button>
        ))}
      </div>
    </div>
  );
}
